let ctx: AudioContext | null = null;
let muted = false;

export type Mood = 'salt' | 'glass' | 'grease' | 'court' | 'marrow' | 'leak' | 'kettle' | 'sink' | 'dark' | 'brights';

import { BASES } from '../shared/items.ts';

type Shape = 'square' | 'saw' | 'noise' | 'slide';

const beds = new Map<Mood, { src: AudioBufferSourceNode; gain: GainNode }>();
let moodNow: Mood | null = null;

export function setMuted(next: boolean): void {
  muted = next;
  if (muted) setMood(null);
}

export function isMuted(): boolean {
  return muted;
}

function ac(): AudioContext | null {
  if (muted) return null;
  try {
    if (!ctx) ctx = new AudioContext();
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

/** Short 8-bit burst. The low sample rate is the crush. */
function crunch(freq: number, dur: number, shape: Shape, gain = 0.07, slide = 1): void {
  const audio = ac();
  if (!audio) return;
  const rate = 8000;
  const len = Math.max(1, Math.floor(rate * dur));
  const buffer = audio.createBuffer(1, len, rate);
  const data = buffer.getChannelData(0);
  let phase = 0;
  let noise = 1;
  for (let i = 0; i < len; i++) {
    const t = i / len;
    const env = (1 - t) * (t < 0.02 ? t / 0.02 : 1);
    const f = freq * (1 + (slide - 1) * t);
    let sample = 0;
    if (shape === 'noise') {
      noise = ((noise * 17) ^ (i * 13)) & 255;
      sample = noise / 128 - 1;
    } else if (shape === 'saw') {
      sample = phase * 2 - 1;
    } else {
      sample = phase < 0.5 ? 0.8 : -0.8;
    }
    sample = Math.round(sample * 7) / 7;
    data[i] = sample * env;
    phase += f / rate;
    phase -= Math.floor(phase);
  }
  const src = audio.createBufferSource();
  src.buffer = buffer;
  const amp = audio.createGain();
  amp.gain.value = gain;
  src.connect(amp);
  amp.connect(audio.destination);
  src.start();
}

export function tone(freq: number, dur: number, type: OscillatorType, gain = 0.045): void {
  const audio = ac();
  if (!audio) return;
  const osc = audio.createOscillator();
  const amp = audio.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  amp.gain.value = gain;
  amp.gain.exponentialRampToValueAtTime(0.0001, audio.currentTime + dur);
  osc.connect(amp);
  amp.connect(audio.destination);
  osc.start();
  osc.stop(audio.currentTime + dur);
}

/** The noise a weapon makes when you swing or fire it. Fists pass null. */
export function sfxWeapon(id: string | null | undefined): void {
  // New pieces borrow the voice of the closest old one.
  const art = id ? BASES[id]?.art : undefined;
  const voice: Record<string, string> = { pipe: 'wrench', maul: 'tooth', axe: 'blade', machete: 'blade', hammer: 'tooth', saw: 'blade', spike: 'keel', scatter: 'pop', rifle: 'rod', box: 'prong', can: id ?? 'pop' };
  if (id && !['wrench', 'blade', 'keel', 'tooth', 'dipper', 'zip', 'coil', 'prong', 'prod', 'needle', 'pop', 'bell', 'suck', 'rod'].includes(id)) {
    if (id === 'lamprifle' || id === 'lastlamp') id = 'needle';
    else if (id === 'mastbolt') id = 'prong';
    else if (art && voice[art]) id = voice[art];
    if (art === 'maul' || art === 'hammer' || art === 'spike') crunch(60, 0.1, 'noise', 0.05);
  }
  switch (id) {
    case 'wrench':
      crunch(220, 0.04, 'square', 0.06);
      crunch(90, 0.09, 'square', 0.05);
      return;
    case 'blade':
      crunch(520, 0.07, 'saw', 0.05, 1.4);
      return;
    case 'keel':
      crunch(740, 0.05, 'square', 0.05);
      crunch(180, 0.06, 'noise', 0.04);
      return;
    case 'tooth':
      crunch(140, 0.08, 'saw', 0.06, 0.6);
      return;
    case 'dipper':
      crunch(620, 0.1, 'square', 0.04);
      crunch(880, 0.14, 'square', 0.035);
      return;
    case 'zip':
      crunch(280, 0.045, 'square', 0.06);
      crunch(160, 0.07, 'saw', 0.03);
      return;
    case 'coil':
      crunch(640, 0.04, 'square', 0.05);
      crunch(360, 0.06, 'saw', 0.04, 0.7);
      return;
    case 'prong':
      crunch(820, 0.05, 'square', 0.045);
      crunch(200, 0.07, 'noise', 0.04);
      return;
    case 'prod':
      crunch(900, 0.03, 'square', 0.05);
      crunch(480, 0.08, 'square', 0.03);
      return;
    case 'needle':
      crunch(1100, 0.09, 'saw', 0.04, 1.6);
      return;
    case 'pop':
      crunch(110, 0.08, 'square', 0.07);
      return;
    case 'bell':
      crunch(740, 0.08, 'square', 0.04);
      crunch(980, 0.12, 'square', 0.03);
      return;
    case 'suck':
      crunch(420, 0.16, 'saw', 0.05, 0.35);
      return;
    case 'rod':
      crunch(150, 0.08, 'square', 0.07);
      crunch(90, 0.1, 'saw', 0.04);
      return;
    default:
      crunch(120, 0.06, 'square', 0.06);
  }
}

/** Something connected with a creature. */
export function sfxSmack(big: boolean): void {
  if (big) {
    crunch(70, 0.1, 'square', 0.08);
    crunch(40, 0.12, 'noise', 0.04);
    return;
  }
  crunch(180, 0.05, 'square', 0.06);
  crunch(90, 0.04, 'noise', 0.03);
}

/** A creature landed a hit on you. */
export function sfxBite(): void {
  crunch(80, 0.09, 'saw', 0.07, 0.5);
  crunch(50, 0.06, 'noise', 0.04);
}

/** A grenade or beam arrived. */
export function sfxBloom(kind: string): void {
  if (kind === 'pop') {
    crunch(90, 0.16, 'noise', 0.08);
    crunch(50, 0.18, 'square', 0.05);
    return;
  }
  if (kind === 'bell') {
    crunch(520, 0.14, 'square', 0.04);
    crunch(780, 0.18, 'square', 0.03);
    return;
  }
  if (kind === 'suck') {
    crunch(300, 0.2, 'saw', 0.05, 0.25);
    return;
  }
  if (kind === 'laser') crunch(1400, 0.04, 'square', 0.03);
}

/** Something good hit the ground. The better it is, the higher it rings. */
export function sfxLoot(rarity: string): void {
  if (rarity === 'odd') {
    tone(660, 0.09, 'triangle', 0.035);
    return;
  }
  if (rarity === 'wrong') {
    tone(660, 0.08, 'triangle', 0.04);
    setTimeout(() => tone(990, 0.12, 'triangle', 0.035), 70);
    return;
  }
  if (rarity === 'relic') {
    tone(520, 0.1, 'triangle', 0.04);
    setTimeout(() => tone(780, 0.1, 'triangle', 0.04), 80);
    setTimeout(() => tone(1040, 0.18, 'triangle', 0.035), 160);
  }
}

export function sfxCoin(): void {
  crunch(1200, 0.03, 'square', 0.03);
  crunch(1600, 0.04, 'square', 0.02);
}

export function sfx(kind: 'melee' | 'shoot' | 'hurt' | 'buy' | 'quest' | 'death' | 'ui'): void {
  switch (kind) {
    case 'melee':
      sfxWeapon(null);
      break;
    case 'shoot':
      sfxWeapon('zip');
      break;
    case 'hurt':
      sfxBite();
      break;
    case 'buy':
      tone(520, 0.07, 'triangle', 0.04);
      break;
    case 'quest':
      tone(440, 0.08, 'triangle', 0.04);
      tone(660, 0.1, 'triangle', 0.03);
      break;
    case 'death':
      crunch(70, 0.28, 'saw', 0.07, 0.4);
      break;
    case 'ui':
      tone(300, 0.04, 'square', 0.02);
      break;
  }
}

function bedBuffer(audio: AudioContext, mood: Mood): AudioBuffer {
  const rate = 8000;
  const seconds = mood === 'leak' ? 1.6 : 2;
  const len = rate * seconds;
  const buffer = audio.createBuffer(1, len, rate);
  const data = buffer.getChannelData(0);
  let noise = 1;
  for (let i = 0; i < len; i++) {
    noise = ((noise * 13) ^ (i * 9)) & 255;
    const n = noise / 128 - 1;
    const t = i / rate;
    if (mood === 'salt') {
      data[i] = n * (0.2 + 0.1 * Math.sin(t * 3));
    } else if (mood === 'glass') {
      data[i] = (Math.floor(t * 9) % 6 === 0 ? 0.7 : n * 0.08);
    } else if (mood === 'grease') {
      const bubble = Math.floor(t * 2) % 2 === 0 && i % 400 < 80 ? n : n * 0.15;
      data[i] = bubble * 0.5;
    } else if (mood === 'court') {
      data[i] = (Math.floor(t * 55) % 2 === 0 ? 0.35 : -0.35) + n * 0.08;
    } else if (mood === 'marrow') {
      data[i] = i % 900 < 40 ? n * 0.8 : n * 0.05;
    } else if (mood === 'kettle') {
      const hiss = Math.sin(t * 0.8) > 0.6 ? n * 0.35 : n * 0.06;
      data[i] = hiss + (i % 2000 < 30 ? 0.4 : 0);
    } else if (mood === 'sink') {
      data[i] = (i % 1300 < 60 ? Math.sin(i * 0.05) * 0.5 : 0) + n * 0.05;
    } else if (mood === 'dark') {
      data[i] = (Math.floor(t * 50) % 2 === 0 ? 0.18 : -0.18) * (Math.floor(t) % 2 === 0 ? 1 : 0.3) + n * 0.03;
    } else if (mood === 'brights') {
      data[i] = (Math.floor(t * 7) % 5 === 0 ? Math.sin(i * 0.3) * 0.4 : 0) + n * 0.05;
    } else if (mood === 'leak') {
      const click = i % 1100 < 12 || i % 1700 < 8;
      data[i] = click ? (i % 2 === 0 ? 0.45 : -0.45) : n * 0.03;
    } else {
      data[i] = n * 0.25;
    }
    data[i] = Math.round(data[i] * 7) / 7;
  }
  return buffer;
}

function ensureBeds(audio: AudioContext): void {
  if (beds.size > 0) return;
  const moods: Mood[] = ['salt', 'glass', 'grease', 'court', 'marrow', 'leak', 'kettle', 'sink', 'dark', 'brights'];
  for (const mood of moods) {
    const src = audio.createBufferSource();
    src.buffer = bedBuffer(audio, mood);
    src.loop = true;
    const gain = audio.createGain();
    gain.gain.value = 0;
    src.connect(gain);
    gain.connect(audio.destination);
    src.start();
    beds.set(mood, { src, gain });
  }
}

/** Quiet area bed. One at a time, and only where a place has a sound. Not music. */
export function setMood(mood: Mood | null): void {
  if (muted) {
    moodNow = null;
    for (const bed of beds.values()) bed.gain.gain.value = 0;
    return;
  }
  if (mood === moodNow) return;
  const audio = ac();
  if (!audio) return;
  if (beds.size === 0 && mood === null) {
    moodNow = null;
    return;
  }
  if (mood !== null) ensureBeds(audio);
  moodNow = mood;
  for (const [name, bed] of beds) {
    bed.gain.gain.setTargetAtTime(name === mood ? 0.014 : 0, audio.currentTime, 0.6);
  }
}

// ---------------------------------------------------------------------------
// Music. Two loops made by tools/make-music.py, kept quiet under everything else.

export type Track = 'explore' | 'boss';
const MUSIC_GAIN: Record<Track, number> = { explore: 0.08, boss: 0.12 };
const tracks = new Map<Track, { gain: GainNode; buffer: AudioBuffer | null; src: AudioBufferSourceNode | null }>();
let musicOn = true;
let trackNow: Track | null = null;
let musicFailedAt = -Infinity;

export function setMusicOn(next: boolean): void {
  musicOn = next;
}

export function isMusicOn(): boolean {
  return musicOn;
}

function startTrack(audio: AudioContext, slot: { gain: GainNode; buffer: AudioBuffer | null; src: AudioBufferSourceNode | null }): void {
  if (!slot.buffer) return;
  slot.src?.stop();
  const src = audio.createBufferSource();
  src.buffer = slot.buffer;
  src.loop = true;
  src.connect(slot.gain);
  src.start();
  slot.src = src;
}

/** Called every frame. Crossfades to the wanted loop; the boss loop starts from its top each fight. */
export function setMusic(track: Track | null): void {
  const want = muted || !musicOn ? null : track;
  if (want === trackNow) return;
  if (want && !tracks.has(want) && performance.now() - musicFailedAt < 10000) return;
  const audio = want ? ac() : ctx;
  if (!audio) return;
  trackNow = want;
  for (const [name, slot] of tracks) {
    if (name === want && name === 'boss') startTrack(audio, slot);
    slot.gain.gain.setTargetAtTime(name === want ? MUSIC_GAIN[name] : 0, audio.currentTime, name === want ? 0.6 : 0.4);
  }
  if (want && !tracks.has(want)) {
    const gain = audio.createGain();
    gain.gain.value = 0;
    gain.connect(audio.destination);
    const slot = { gain, buffer: null as AudioBuffer | null, src: null as AudioBufferSourceNode | null };
    tracks.set(want, slot);
    fetch(`/music/${want}.mp3`)
      .then((res) => (res.ok ? res.arrayBuffer() : Promise.reject(new Error(String(res.status)))))
      .then((bytes) => audio.decodeAudioData(bytes))
      .then((buffer) => {
        slot.buffer = buffer;
        startTrack(audio, slot);
        if (trackNow === want) gain.gain.setTargetAtTime(MUSIC_GAIN[want], audio.currentTime, 0.6);
      })
      .catch(() => {
        // Try again in a bit (say the server was mid-restart) instead of staying silent until a toggle.
        tracks.delete(want);
        musicFailedAt = performance.now();
        if (trackNow === want) trackNow = null;
      });
  }
}
